import { useState } from 'react'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  Alert,
  Box,
  Divider,
  Grid,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'

import {
  COLOR_LAST_BID,
  COLOR_LAST_COMPLETED_JOB,
  NETWORK_COUNT_INFO,
  SUPPLIER_ACTIVITY_RECENCY_DEFAULT_WINDOW_DAYS,
  SUPPLIER_ACTIVITY_RECENCY_FOOTNOTES,
  SUPPLIER_ACTIVITY_RECENCY_INFO,
  SUPPLIER_ACTIVITY_RECENCY_WINDOWS,
} from 'components/reports/Metrics/constants/SupplierActivityRecency'
import {
  ActivityAgeBand,
  useGetSupplierActivityMeanSeriesQuery,
  useGetSupplierActivityRecencyQuery,
  VehicleTypeFilter,
} from 'generated/graphql'

import { VEHICLE_TYPES } from '../constants/vehicleTypes'
import { MeanDailyValuesCard } from './MeanDailyValuesCard'
import { RecencyCumulativeCard } from './RecencyCumulativeCard'
import { SupplierActivityRecencyExportButton } from './SupplierActivityRecencyExportButton'

export const SupplierActivityRecency = () => {
  const [vehicleType, setVehicleType] = useState(VehicleTypeFilter.All)
  const [windowDays, setWindowDays] = useState(SUPPLIER_ACTIVITY_RECENCY_DEFAULT_WINDOW_DAYS)
  const [band, setBand] = useState(ActivityAgeBand.Months0To3)

  const queryOptions = {
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-and-network' as const,
  }

  const recency = useGetSupplierActivityRecencyQuery({
    variables: { input: { vehicleType, windowDays } },
    ...queryOptions,
  })

  const meanSeries = useGetSupplierActivityMeanSeriesQuery({
    variables: { input: { vehicleType, band } },
    ...queryOptions,
  })

  const data =
    recency.data?.supplierActivityRecency ?? recency.previousData?.supplierActivityRecency
  const series =
    meanSeries.data?.supplierActivityMeanSeries ??
    meanSeries.previousData?.supplierActivityMeanSeries
  const loading = recency.loading && !data

  const windowLabel =
    SUPPLIER_ACTIVITY_RECENCY_WINDOWS.find((option) => option.value === windowDays)?.label ??
    `${windowDays} days`

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent='space-between'
        alignItems={{ xs: 'stretch', md: 'flex-start' }}>
        <Box sx={{ maxWidth: 460 }}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography variant='h5' fontWeight={700} sx={{ color: 'primary.main' }}>
              Supplier Activity Recency
            </Typography>
            <Tooltip
              title={SUPPLIER_ACTIVITY_RECENCY_INFO}
              slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 420 } } }}>
              <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
            </Tooltip>
          </Stack>
          <Typography variant='body2' color='text.secondary'>
            Understand how recently suppliers have bid or completed jobs to identify at-risk and
            dormant suppliers.
          </Typography>
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            select
            size='small'
            label='Period'
            value={windowDays}
            onChange={(event) => setWindowDays(Number(event.target.value))}
            sx={{ minWidth: 140 }}>
            {SUPPLIER_ACTIVITY_RECENCY_WINDOWS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size='small'
            label='Vehicle type'
            value={vehicleType}
            onChange={(event) => setVehicleType(event.target.value as VehicleTypeFilter)}
            sx={{ minWidth: 170 }}>
            {VEHICLE_TYPES.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <Divider orientation='vertical' flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />

          <Box textAlign={{ xs: 'left', sm: 'right' }}>
            <Stack
              direction='row'
              spacing={0.5}
              alignItems='center'
              justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
              <Typography variant='caption' color='text.secondary' whiteSpace='nowrap'>
                Total suppliers in network
              </Typography>
              <Tooltip
                title={NETWORK_COUNT_INFO}
                slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 420 } } }}>
                <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
              </Tooltip>
            </Stack>
            {loading ? (
              <Skeleton width={70} height={32} />
            ) : (
              <Typography variant='h6' fontWeight={700}>
                {(data?.networkSupplierCount ?? 0).toLocaleString()}
              </Typography>
            )}
          </Box>
        </Stack>
      </Stack>

      {recency.error && (
        <Alert severity='error'>Could not load the supplier activity metrics.</Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <RecencyCumulativeCard
            title='Time Since Last Bid (Cumulative)'
            subtitle='Cumulative % of suppliers by time elapsed since last bid'
            info={
              'Shows the cumulative percentage of suppliers whose most recent bid was within ' +
              'the number of days shown on the axis. Suppliers who have never placed a bid and ' +
              'Courier Exchange suppliers are excluded. Percentages are based only on suppliers ' +
              'who have placed at least one bid.'
            }
            xAxisLabel='Days since last bid (before today)'
            eligibleCaption='Suppliers with at least one bid'
            outsideWindowCaption={`Suppliers with no bids in ${windowLabel}+`}
            color={COLOR_LAST_BID}
            metric={data?.lastBid}
            networkSupplierCount={data?.networkSupplierCount}
            loading={loading}
          />
        </Grid>

        <Grid item xs={12}>
          <RecencyCumulativeCard
            title='Time Since Last Completed Job (Cumulative)'
            subtitle='Cumulative % of suppliers by time elapsed since last completed job'
            info={
              'Shows the cumulative percentage of suppliers who completed their most recent job ' +
              'within the number of days shown on the axis. A job is considered completed when ' +
              'the final delivery is signed for. Suppliers who have never completed a job and ' +
              'Courier Exchange suppliers are excluded. Percentages are calculated using only ' +
              'suppliers with at least one completed job.'
            }
            xAxisLabel='Days since last completed job (before today)'
            eligibleCaption='Suppliers with at least one completed job'
            outsideWindowCaption={`Suppliers with no completed jobs in ${windowLabel}+`}
            color={COLOR_LAST_COMPLETED_JOB}
            metric={data?.lastCompletedJob}
            networkSupplierCount={data?.networkSupplierCount}
            loading={loading}
          />
        </Grid>

        <Grid item xs={12}>
          <MeanDailyValuesCard
            band={band}
            onBandChange={setBand}
            data={series}
            loading={meanSeries.loading && !series}
          />
        </Grid>
      </Grid>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent='space-between'
        alignItems={{ xs: 'stretch', sm: 'center' }}>
        <Stack
          direction='row'
          spacing={1.25}
          alignItems='flex-start'
          sx={{
            px: 2,
            py: 1.5,
            borderRadius: 2,
            bgcolor: alpha(COLOR_LAST_BID, 0.04),
            border: 1,
            borderColor: alpha(COLOR_LAST_BID, 0.2),
          }}>
          <InfoOutlinedIcon sx={{ fontSize: 18, color: COLOR_LAST_BID, mt: 0.25 }} />
          <Box>
            {SUPPLIER_ACTIVITY_RECENCY_FOOTNOTES.map((line) => (
              <Typography
                key={line}
                variant='caption'
                color='text.secondary'
                display='block'
                sx={{ lineHeight: 1.7 }}>
                {line}
              </Typography>
            ))}
          </Box>
        </Stack>

        <SupplierActivityRecencyExportButton vehicleType={vehicleType} windowDays={windowDays} />
      </Stack>
    </Stack>
  )
}
